import { ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  stockNewsAPI,
  newsAPI,
  NewsPortalData,
  NewsArticleSummary,
  BookmarkItemType,
} from '../../services/api';
import {
  Activity,
  ChevronRight,
  Filter,
  Loader2,
  Newspaper,
  Search,
} from 'lucide-react';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkBatchProvider from '../../components/BookmarkBatchProvider';

// ============================================================================
// HELPERS
// ============================================================================

function articleUrl(article: NewsArticleSummary): string {
  return newsAPI.getArticleHtmlUrl(article.source, article.id);
}

function bookmarkType(source: string): BookmarkItemType {
  if (source === 'economic') return 'news_economic';
  if (source === 'events') return 'news_events';
  return 'news_earnings';
}

// ============================================================================
// LEAD STORY
// ============================================================================

function LeadStory({ article }: { article: NewsArticleSummary }): ReactElement {
  return (
    <div className="group block relative">
      <div className="absolute top-3 right-3 z-10 bg-black/40 backdrop-blur-sm rounded-lg">
        <BookmarkButton
          itemType={bookmarkType(article.source)}
          itemId={String(article.id)}
          title={article.title}
          subtitle={article.symbol ?? undefined}
          url={articleUrl(article)}
          size="md"
          className="text-white hover:text-amber-300"
        />
      </div>
      <a href={articleUrl(article)} className="block">
        <div className="relative rounded-xl overflow-hidden mb-4">
          <img
            src="/images/stocks/hero.jpg"
            alt="Stock News"
            className="w-full h-48 sm:h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-300">
              {article.section}
              {article.symbol && (
                <>
                  {' '}&middot; <span className="text-slate-300">{article.symbol}</span>
                </>
              )}
              {article.key_stat && (
                <>
                  {' '}&middot; <span className="text-emerald-400">{article.key_stat}</span>
                </>
              )}
            </span>
            <h2
              className="text-lg sm:text-2xl font-bold text-white group-hover:text-gray-200 transition-colors leading-snug mt-1"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              {article.title}
            </h2>
          </div>
        </div>
        {article.summary && (
          <p className="text-sm text-gray-500 leading-relaxed mb-2">{article.summary}</p>
        )}
        <span className="text-xs text-gray-400">{article.time_ago}</span>
      </a>
    </div>
  );
}

// ============================================================================
// FEED ROW (top feed and "more stories")
// ============================================================================

function FeedRow({ article }: { article: NewsArticleSummary }): ReactElement {
  return (
    <div className="group relative flex items-start gap-3 p-3.5 rounded-lg bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-150">
      <a href={articleUrl(article)} className="flex-1 min-w-0 block">
        <h3 className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 transition-colors leading-snug line-clamp-2 mb-1.5">
          {article.title}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-medium uppercase text-gray-400">{article.section}</span>
          {article.symbol && (
            <>
              <span className="text-gray-200">&middot;</span>
              <span className="text-[10px] font-mono font-medium text-slate-500">{article.symbol}</span>
            </>
          )}
          {article.key_stat && (
            <>
              <span className="text-gray-200">&middot;</span>
              <span className="text-[10px] font-mono font-medium text-emerald-600">{article.key_stat}</span>
            </>
          )}
          <span className="text-gray-200">&middot;</span>
          <span className="text-[10px] text-gray-400">{article.time_ago}</span>
        </div>
      </a>
      <BookmarkButton
        itemType={bookmarkType(article.source)}
        itemId={String(article.id)}
        title={article.title}
        subtitle={article.symbol ?? undefined}
        url={articleUrl(article)}
        size="sm"
      />
    </div>
  );
}

// ============================================================================
// SECTION ROW (compact, used in section blocks)
// ============================================================================

function SectionRow({ article }: { article: NewsArticleSummary }): ReactElement {
  return (
    <div className="group flex items-start gap-2.5 py-2.5">
      <a href={articleUrl(article)} className="flex-1 min-w-0 block">
        <h3 className="text-[13px] font-medium text-gray-700 group-hover:text-gray-900 transition-colors leading-snug line-clamp-2">
          {article.title}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-400">
          <span className="font-medium uppercase">{article.section}</span>
          {article.symbol && (
            <>
              <span>&middot;</span>
              <span className="font-mono text-slate-500">{article.symbol}</span>
            </>
          )}
          <span>&middot;</span>
          <span>{article.time_ago}</span>
        </div>
      </a>
      <BookmarkButton
        itemType={bookmarkType(article.source)}
        itemId={String(article.id)}
        title={article.title}
        subtitle={article.symbol ?? undefined}
        url={articleUrl(article)}
        size="sm"
      />
    </div>
  );
}

// ============================================================================
// SECTION BLOCK (one card per section)
// ============================================================================

const SECTION_IMAGES: Record<string, string> = {
  markets: '/images/stocks/markets.jpg',
  stocks: '/images/stocks/stocks.jpg',
  earnings: '/images/stocks/earnings.jpg',
  'sec-filings': '/images/stocks/sec-filings.jpg',
  editorial: '/images/stocks/editorial.jpg',
  'trading-sessions': '/images/stocks/trading-sessions.jpg',
};

function SectionBlock({
  sectionKey,
  data,
}: {
  sectionKey: string;
  data: { name: string; articles: NewsArticleSummary[]; total_count: number };
}): ReactElement {
  if (data.articles.length === 0) return <></>;

  const imageUrl = SECTION_IMAGES[sectionKey];

  return (
    <div>
      {imageUrl ? (
        <div className="relative rounded-lg overflow-hidden mb-3">
          <img src={imageUrl} alt={data.name} className="w-full h-28 sm:h-36 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5">
            <h2 className="text-xs font-bold text-white uppercase tracking-wide">{data.name}</h2>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">{data.name}</h2>
        </div>
      )}
      {data.total_count > data.articles.length && (
        <div className="flex justify-end mb-2">
          <Link
            to={`/stocks/news/${sectionKey}`}
            className="flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-500 transition-colors"
          >
            More
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
      <div className="divide-y divide-gray-50">
        {data.articles.map((article) => (
          <SectionRow key={`${article.source}-${article.id}`} article={article} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PORTAL
// ============================================================================

const SECTION_ORDER = [
  'markets',
  'stocks',
  'earnings',
  'sec-filings',
  'editorial',
  'trading-sessions',
];

export default function StocksPortal(): ReactElement {
  const navigate = useNavigate();

  const { data: portalData, isLoading, error } = useQuery<NewsPortalData>({
    queryKey: ['stocks-news-portal'],
    queryFn: async () => {
      const response = await stockNewsAPI.getPortalData(15);
      return response.data;
    },
    staleTime: 0,
  });

  const sectionKeys = portalData
    ? SECTION_ORDER.filter((k) => portalData.sections[k]?.articles?.length)
    : [];

  // Batch all visible article bookmark-status checks into one request.
  const batchItems = portalData
    ? [
        ...(portalData.lead ? [{ itemType: bookmarkType(portalData.lead.source), itemId: String(portalData.lead.id) }] : []),
        ...portalData.feed.map((a) => ({
          itemType: bookmarkType(a.source),
          itemId: String(a.id),
        })),
        ...sectionKeys.flatMap((k) =>
          portalData.sections[k].articles.map((a) => ({
            itemType: bookmarkType(a.source),
            itemId: String(a.id),
          }))
        ),
      ]
    : [];

  return (
    <BookmarkBatchProvider items={batchItems}>
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">

        {/* Search Bar — symbol jump */}
        <div className="mb-6">
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by symbol or company name..."
              className="w-full pl-12 pr-28 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all text-sm shadow-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const value = (e.target as HTMLInputElement).value.toUpperCase();
                  if (value) navigate(`/stocks/${value}`);
                }
              }}
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-gray-400 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
            </div>
          </div>
        </div>

        {/* Two link cards: Market Context + Strategies */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Link
            to="/stocks/market"
            className="group flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-sm shadow-cyan-500/25">
                <Activity className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">Market Context</h3>
                <p className="text-[11px] text-gray-500 mt-0.5">Indices, breadth, sectors, movers</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
          </Link>

          <Link
            to="/stocks/strategies"
            className="group flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-gray-300 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-sm shadow-emerald-500/25">
                <Filter className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">Strategies & Screens</h3>
                <p className="text-[11px] text-gray-500 mt-0.5">Flagship strategies and saved screens</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
          </Link>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" />
            <p className="text-sm text-gray-500">Loading stock news...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <Newspaper className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Failed to load stock news</h3>
            <p className="text-sm text-gray-500">Please try again later</p>
          </div>
        )}

        {/* Portal content */}
        {portalData && !isLoading && (
          <>
            {/* Top: Lead (left) + Latest feed (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-5 mb-6 sm:mb-8">
              {/* Lead spans 3 of 5 cols */}
              <div className="lg:col-span-3">
                {portalData.lead && <LeadStory article={portalData.lead} />}
                {portalData.feed.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4">
                    {portalData.feed.slice(0, 6).map((article) => (
                      <FeedRow key={`sub-${article.source}-${article.id}`} article={article} />
                    ))}
                  </div>
                )}
              </div>

              {/* Latest sidebar spans 2 of 5 cols */}
              <div className="lg:col-span-2 space-y-2">
                {portalData.feed.slice(6, 12).map((article) => (
                  <FeedRow key={`side-${article.source}-${article.id}`} article={article} />
                ))}
              </div>
            </div>

            {/* More stories — two-column, even count */}
            {portalData.feed.length > 12 && (() => {
              const remaining = portalData.feed.slice(12);
              const evenItems = remaining.slice(0, remaining.length % 2 === 0 ? remaining.length : remaining.length - 1);
              return (
                <div className="mb-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {evenItems.map((article) => (
                      <FeedRow key={`${article.source}-${article.id}`} article={article} />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Section grid (2-column) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 pt-5 sm:pt-6 border-t border-gray-200">
              {sectionKeys.map((sectionKey) => {
                const sectionData = portalData.sections[sectionKey];
                if (!sectionData || sectionData.articles.length === 0) return null;
                return (
                  <SectionBlock
                    key={sectionKey}
                    sectionKey={sectionKey}
                    data={sectionData}
                  />
                );
              })}
            </div>

            {/* Empty state */}
            {portalData.total_articles === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                  <Newspaper className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No stock news yet</h3>
                <p className="text-sm text-gray-500">Check back later</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </BookmarkBatchProvider>
  );
}
